<script>
export default {
  name: "modal-csv-import",
  data() {
    return {
      id: this.$A.GetUUID(),
      csvFile: null,
      csvData: [],
      csvHeaders: [],
      columnMapping: {},
      isLoading: false,
      isImporting: false,
      importProgress: 0,
      importResults: {
        total: 0,
        successful: 0,
        failed: 0,
        errors: []
      },
      showResults: false,
      isDragOver: false,
      currentStep: 1, // 1: Upload, 2: Map Columns, 3: Preview, 4: Results
      importMode: 'create', // 'create' or 'update' 
      existingContacts: [], // Loaded when update mode is selected
      isLoadingContacts: false,
      rateLimitDelay: 50, // milliseconds between API calls
      maxRetries: 3,
      batchSize: 10, // Process contacts in batches
      requiredFields: {
        name: { label: 'Name', required: true },
        email: { label: 'Email', required: true },
        frequency: { label: 'Days (e.g., Sun,Mon,Tue,Wed,Thu,Fri,Sat)', required: false, default: null }, // Will be set from user settings
        timeOfDayToSend: { label: 'Time (e.g., 09:00)', required: false, default: null }, // Will be set from user settings
        timeZone: { label: 'Time Zone', required: false, default: null } // Will be set from user settings
      }
    };
  },
  computed: {
    hasValidFile() {
      return this.csvFile && this.csvData.length > 0;
    },
    hasValidMapping() {
      const mappedProperties = Object.values(this.columnMapping).filter(v => v);
      return mappedProperties.includes('name') && mappedProperties.includes('email');
    },
    canImport() {
      return this.hasValidFile && this.hasValidMapping && !this.isImporting;
    },
    previewData() {
      return this.csvData.slice(0, 5); // Show first 5 rows for preview
    },
    unmappedRequiredFields() {
      const mappedProperties = Object.values(this.columnMapping).filter(v => v);
      return Object.keys(this.requiredFields).filter(field => 
        this.requiredFields[field].required && !mappedProperties.includes(field)
      );
    },
    unmappedColumnsCount() {
      return Object.values(this.columnMapping).filter(value => value === null || value === undefined).length;
    },
    modalTitle() {
      const mode = this.importMode === 'create' ? 'Create' : 'Update';
      if (this.currentStep === 4 || this.showResults) {
        return `${mode} Results`;
      } else if (this.isImporting) {
        return `${this.importMode === 'create' ? 'Creating' : 'Updating'} Contacts...`;
      } else if (this.currentStep === 3) {
        return `Preview ${mode} Data`;
      } else if (this.currentStep === 2) {
        return 'Map CSV Columns';
      } else {
        return `${mode} Contacts from CSV`;
      }
    },
    canGoNext() {
      if (this.currentStep === 1) {
        return this.hasValidFile && !this.isLoading;
      } else if (this.currentStep === 2) {
        return this.hasValidMapping;
      }
      return false;
    },
    canGoBack() {
      return this.currentStep > 1 && !this.isImporting;
    },
    adjustedRateLimit() {
      // Increase delay for larger imports to be more conservative
      const contactCount = this.csvData.length;
      let baseDelay = this.rateLimitDelay;
      
      // Size-based rate limiting
      if (contactCount > 1000) {
        baseDelay = this.rateLimitDelay * 3; // 300ms for very large imports
      } else if (contactCount > 500) {
        baseDelay = this.rateLimitDelay * 2; // 200ms for large imports
      } else if (contactCount > 100) {
        baseDelay = this.rateLimitDelay * 1.5; // 150ms for medium imports
      }
      
      // Update mode makes 2x API calls (GET + PATCH), so double the delay for safety
      if (this.importMode === 'update') {
        baseDelay = baseDelay * 2;
      }
      
      return baseDelay;
    },
    validationErrors() {
      return this.importResults.errors.filter(error => 
        this.isValidationError(error.error)
      );
    },
    networkErrors() {
      return this.importResults.errors.filter(error => 
        !this.isValidationError(error.error)
      );
    },
    userDefaultFrequency() {
      // Get frequency from user's reminder settings
      const userSettings = this.$A.Auth?.userSettings;
      if (userSettings && userSettings.reminderDays) {
        return userSettings.reminderDays;
      }
      // Fallback to all days if no user settings
      return 'Sun, Mon, Tue, Wed, Tr, Fri, Sat';
    },
    userDefaultTimeOfDay() {
      // Get time of day from user's reminder settings
      const userSettings = this.$A.Auth?.userSettings;
      if (userSettings && userSettings.reminderTimeOfDay) {
        return userSettings.reminderTimeOfDay;
      }
      // Fallback to 9 AM
      return '09:00';
    },
    userDefaultTimeZone() {
      // Get timezone from user's reminder settings
      const userSettings = this.$A.Auth?.userSettings;
      if (userSettings && userSettings.reminderTimeZone) {
        return userSettings.reminderTimeZone;
      }
      // Fallback to UTC
      return 'UTC';
    }
  },
  watch: {
    csvFile(newFile, oldFile) {
      // Handle drag-and-drop file changes
      if (newFile && newFile !== oldFile) {
        this.handleFileUpload({ target: null });
      }
    }
  },
  methods: {
    open() {
      this.resetModal();
      this.$bvModal.show(this.id);
    },
    resetModal() {
      this.csvFile = null;
      this.csvData = [];
      this.csvHeaders = [];
      this.columnMapping = {};
      this.isLoading = false;
      this.isImporting = false;
      this.importProgress = 0;
      this.importResults = {
        total: 0,
        successful: 0,
        failed: 0,
        errors: []
      };
      this.showResults = false;
      this.isDragOver = false;
      this.currentStep = 1;
      this.importMode = 'create';
      this.existingContacts = [];
      this.isLoadingContacts = false;
      
      // Clear the file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },
    async onImportModeChange(newMode) {
      if (newMode === 'update') {
        await this.loadExistingContacts();
      }
    },
    async loadExistingContacts() {
      try {
        this.isLoadingContacts = true;
        this.existingContacts = await this.$A.ApproverService.AllIndexed();
        console.log(`Loaded ${this.existingContacts.length} existing contacts for matching`);
      } catch (error) {
        console.error('Error loading existing contacts:', error);
        this.$A.AlertUser('Failed to load existing contacts. Please try again.', 'warning');
        this.importMode = 'create'; // Fall back to create mode
      } finally {
        this.isLoadingContacts = false;
      }
    },
    async handleFileUpload(event) {
      // Handle both click-to-select and drag-and-drop
      let file = null;
      
      if (event.target && event.target.files && event.target.files[0]) {
        // File selected via click
        file = event.target.files[0];
      } else if (this.csvFile) {
        // File selected via v-model (includes drag-and-drop)
        file = this.csvFile;
      }
      
      if (!file) return;

      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        this.$A.AlertUser('Please select a CSV file', 'warning');
        this.csvFile = null; // Clear invalid file
        return;
      }

      this.isLoading = true;

      try {
        const text = await this.readFileAsText(file);
        this.parseCsv(text);
        this.autoMapColumns();
        // Auto-advance to mapping step after successful file load
        this.currentStep = 2;
      } catch (error) {
        console.error('Error reading CSV file:', error);
        this.$A.AlertUser('Error reading CSV file', 'danger');
        this.csvFile = null; // Clear on error
      } finally {
        this.isLoading = false;
      }
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },
    parseCsv(text) {
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        this.$A.AlertUser('CSV file must have at least a header row and one data row', 'warning');
        return;
      }

      // Parse headers
      this.csvHeaders = this.parseCsvLine(lines[0]);
      
      // Parse data rows
      this.csvData = lines.slice(1).map(line => {
        const values = this.parseCsvLine(line);
        const row = {};
        this.csvHeaders.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      }).filter(row => Object.values(row).some(value => value.trim())); // Remove empty rows
    },
    parseCsvLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i++; // Skip next quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    },
    autoMapColumns() {
      // Auto-map columns based on common naming patterns
      const mapping = {};
      const lowerHeaders = this.csvHeaders.map(h => h.toLowerCase());
      
      // Initialize all headers as unmapped
      this.csvHeaders.forEach(header => {
        mapping[header] = null;
      });
      
      // Map name field
      const namePatterns = ['name', 'full name', 'fullname', 'contact name', 'first name'];
      const nameMatch = namePatterns.find(pattern => 
        lowerHeaders.some(header => header.includes(pattern))
      );
      if (nameMatch) {
        const headerIndex = lowerHeaders.findIndex(header => header.includes(nameMatch));
        mapping[this.csvHeaders[headerIndex]] = 'name';
      }
      
      // Map email field
      const emailPatterns = ['email', 'email address', 'e-mail'];
      const emailMatch = emailPatterns.find(pattern => 
        lowerHeaders.some(header => header.includes(pattern))
      );
      if (emailMatch) {
        const headerIndex = lowerHeaders.findIndex(header => header.includes(emailMatch));
        mapping[this.csvHeaders[headerIndex]] = 'email';
      }

      // Map optional fields - frequency contains days, timeOfDayToSend contains time
      const frequencyPatterns = ['frequency', 'days', 'notification days', 'schedule days'];
      const frequencyMatch = frequencyPatterns.find(pattern => 
        lowerHeaders.some(header => header.includes(pattern))
      );
      if (frequencyMatch) {
        const headerIndex = lowerHeaders.findIndex(header => header.includes(frequencyMatch));
        mapping[this.csvHeaders[headerIndex]] = 'frequency';
      }

      const timePatterns = ['timeOfDayToSend', 'time', 'time of day', 'send time', 'notification time'];
      const timeMatch = timePatterns.find(pattern => 
        lowerHeaders.some(header => header.includes(pattern))
      );
      if (timeMatch) {
        const headerIndex = lowerHeaders.findIndex(header => header.includes(timeMatch));
        mapping[this.csvHeaders[headerIndex]] = 'timeOfDayToSend';
      }

      const timezonePatterns = ['timezone', 'time zone', 'tz'];
      const timezoneMatch = timezonePatterns.find(pattern => 
        lowerHeaders.some(header => header.includes(pattern))
      );
      if (timezoneMatch) {
        const headerIndex = lowerHeaders.findIndex(header => header.includes(timezoneMatch));
        mapping[this.csvHeaders[headerIndex]] = 'timeZone';
      }

      this.columnMapping = mapping;
    },
    async startImport() {
      if (!this.canImport) return;

      this.isImporting = true;
      this.importProgress = 0;
      this.importResults = {
        total: this.csvData.length,
        successful: 0,
        failed: 0,
        errors: []
      };

      // Process contacts in batches with rate limiting
      const batches = this.createBatches(this.csvData, this.batchSize);
      let processedCount = 0;

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Process each contact in the current batch
        for (let contactIndex = 0; contactIndex < batch.length; contactIndex++) {
          const row = batch[contactIndex];
          const globalIndex = processedCount + 1;
          
          try {
            await this.createApproverFromRowWithRetry(row, globalIndex);
            this.importResults.successful++;
          } catch (error) {
            this.importResults.failed++;
            this.importResults.errors.push({
              row: globalIndex,
              data: row,
              error: error.message || 'Unknown error'
            });
          }
          
          processedCount++;
          this.importProgress = Math.round((processedCount / this.csvData.length) * 100);
          
          // Rate limiting: Wait between API calls (except for the last contact)
          if (processedCount < this.csvData.length) {
            await this.delay(this.adjustedRateLimit);
          }
        }
        
        // Longer pause between batches to be extra safe
        if (batchIndex < batches.length - 1) {
          await this.delay(this.adjustedRateLimit * 2);
        }
      }

      this.isImporting = false;
      this.showResults = true;
      this.currentStep = 4;
      
      // Emit event to refresh approver list
      this.$emit('import-completed', this.importResults);
    },
    async createApproverFromRow(row) {
      const rawName = this.getFieldValue(row, 'name');
      const rawEmail = this.getFieldValue(row, 'email');
      
      let approver = {
        name: rawName ? rawName.trim() : '',
        email: rawEmail ? rawEmail.trim().toLowerCase() : '',
        frequency: this.getFieldValue(row, 'frequency') || this.userDefaultFrequency,
        timeOfDayToSend: this.getFieldValue(row, 'timeOfDayToSend') || this.userDefaultTimeOfDay,
        timeZone: this.getFieldValue(row, 'timeZone') || this.userDefaultTimeZone
      };

      // The API's day tokens use 'Tr' for Thursday; CSVs naturally say 'Thu'
      if (approver.frequency) {
        approver.frequency = approver.frequency.replace(/\bThu\b/gi, 'Tr');
      }

      // The API requires zero-padded HH:mm; CSVs often say e.g. '9:00'
      if (approver.timeOfDayToSend) {
        approver.timeOfDayToSend = approver.timeOfDayToSend
          .trim()
          .replace(/^(\d):(\d\d)$/, '0$1:$2');
      }

      // Enhanced validation with detailed error messages
      if (!approver.name) {
        throw new Error('Name field is empty or missing');
      }
      if (approver.name.length < 3) {
        throw new Error(`Name "${approver.name}" is too short (minimum 3 characters)`);
      }
      if (approver.name.length > 100) {
        throw new Error(`Name "${approver.name}" is too long (maximum 100 characters)`);
      }
      
      if (!approver.email) {
        throw new Error('Email field is empty or missing');
      }
      if (!this.$A.IsEmailValid(approver.email)) {
        throw new Error(`Email "${approver.email}" is not a valid email address`);
      }
      if (approver.email.length > 254) {
        throw new Error(`Email "${approver.email}" is too long (maximum 254 characters)`);
      }

      // Validate optional fields - frequency contains days, timeOfDayToSend contains time
      
      // Validate frequency (days) field
      const validFrequencies = [
        'Sun, Mon, Tue, Wed, Tr, Fri, Sat', // All days
        'Mon, Tue, Wed, Tr, Fri', // Weekdays only
        'Sat, Sun', // Weekends only
        'None', // No notifications
        'daily', // Legacy format (will be converted)
        'weekly' // Legacy format (will be converted)
      ];
      
      if (approver.frequency) {
        // Convert legacy formats to new format
        if (approver.frequency === 'daily') {
          approver.frequency = 'Sun, Mon, Tue, Wed, Tr, Fri, Sat';
        } else if (approver.frequency === 'weekly') {
          approver.frequency = 'Mon'; // Default to Monday for weekly
        }
        
        // If it's not a recognized format, validate it contains valid day abbreviations
        if (!validFrequencies.includes(approver.frequency)) {
          const dayPattern = /^(Sun|Mon|Tue|Wed|Tr|Fri|Sat)(,\s*(Sun|Mon|Tue|Wed|Tr|Fri|Sat))*$/;
          if (!dayPattern.test(approver.frequency)) {
            console.warn(`Invalid frequency format "${approver.frequency}", using user default: ${this.userDefaultFrequency}`);
            approver.frequency = this.userDefaultFrequency;
          }
        }
      }
      
      // Validate timeOfDayToSend (time) field
      if (approver.timeOfDayToSend) {
        // Check if it's a valid time format (HH:MM)
        const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timePattern.test(approver.timeOfDayToSend)) {
          console.warn(`Invalid time format "${approver.timeOfDayToSend}", using user default: ${this.userDefaultTimeOfDay}`);
          approver.timeOfDayToSend = this.userDefaultTimeOfDay;
        }
      }

      // Handle create vs update mode
      let existingContact = null;
      if (this.importMode === 'update') {
        // Find existing contact by email
        existingContact = this.existingContacts.find(contact => 
          contact.email.toLowerCase() === approver.email.toLowerCase()
        );
        
        if (!existingContact) {
          console.warn(`Update mode: Contact with email "${approver.email}" not found in existing contacts list`);
          throw new Error(`Contact with email "${approver.email}" not found. Cannot update non-existing contact.`);
        }
        
        console.log(`Update mode: Found existing contact ID ${existingContact.id} for email "${approver.email}"`);
        
        // Load the complete existing contact object (like the UI does)
        console.log('Update mode: Loading complete contact object from API...');
        const fullContact = await this.$A.ApproverService.GetWithStats(existingContact.id);
        
        if (!fullContact || !fullContact.approver) {
          throw new Error(`Failed to load complete contact data for ID ${existingContact.id}`);
        }
        
        // Clone the read-only object to make it writable, then update the fields we want to change
        const clonedApprover = JSON.parse(JSON.stringify(fullContact.approver)); // Deep clone to remove read-only
        approver = Object.assign(clonedApprover, {
          name: approver.name,     // Override with CSV values
          email: approver.email,
          frequency: approver.frequency,
          timeOfDayToSend: approver.timeOfDayToSend,
          timeZone: approver.timeZone
        });
        
        console.log('Update mode: Complete approver object being sent to API:', JSON.stringify({
          id: approver.id,
          name: approver.name,
          email: approver.email,
          frequency: approver.frequency,
          timeOfDayToSend: approver.timeOfDayToSend,
          timeZone: approver.timeZone,
          hasAllOriginalFields: true
        }, null, 2));
      }
      
      // Create or update approver using the service
      console.log(`${this.importMode === 'update' ? 'PATCH' : 'POST'} API call with approver:`, approver);
      const result = await this.$A.ApproverService.Save(approver);
      
      // Handle different response structures for create vs update
      if (this.importMode === 'update') {
        // For updates, if the API call succeeded, return the updated contact info
        if (result) {
          return {
            id: existingContact.id,
            name: approver.name,
            email: approver.email,
            frequency: approver.frequency,
            timeOfDayToSend: approver.timeOfDayToSend,
            timeZone: approver.timeZone,
            ...result
          };
        } else {
          throw new Error('Update operation failed');
        }
      } else {
        // For creates, expect the API to return the full object with ID
        if (!result || !result.id) {
          throw new Error('API returned success but no approver ID was provided');
        }
        return result;
      }
    },
    async createApproverFromRowWithRetry(row, rowNumber) {
      let lastError = null;
      
      for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
        try {
          const result = await this.createApproverFromRow(row);
          
          // If successful on retry, log it
          if (attempt > 1) {
            console.log(`✅ Successfully created contact on attempt ${attempt} for row ${rowNumber}`);
          }
          
          return result;
        } catch (error) {
          lastError = error;
          
          // Log detailed error information for debugging
          console.error(`❌ Attempt ${attempt} failed for row ${rowNumber}:`, {
            error: error,
            message: error.message,
            status: error.status,
            response: error.response,
            contactData: {
              name: this.getFieldValue(row, 'name'),
              email: this.getFieldValue(row, 'email')
            }
          });
          
          // Check if this is a client error (4xx) that shouldn't be retried
          if (this.isClientError(error)) {
            const clientErrorMsg = this.getDetailedErrorMessage(error, row);
            throw new Error(`Validation Error (Row ${rowNumber}): ${clientErrorMsg}`);
          }
          
          // If it's the last attempt, throw the error
          if (attempt === this.maxRetries) {
            const finalErrorMsg = this.getDetailedErrorMessage(error, row);
            throw new Error(`Failed after ${this.maxRetries} attempts (Row ${rowNumber}): ${finalErrorMsg}`);
          }
          
          // Wait with exponential backoff before retrying
          const backoffDelay = this.adjustedRateLimit * Math.pow(2, attempt - 1);
          console.log(`⏳ Retrying row ${rowNumber} in ${backoffDelay}ms... (attempt ${attempt + 1}/${this.maxRetries})`);
          await this.delay(backoffDelay);
        }
      }
    },
    createBatches(array, batchSize) {
      const batches = [];
      for (let i = 0; i < array.length; i += batchSize) {
        batches.push(array.slice(i, i + batchSize));
      }
      return batches;
    },
    delay(milliseconds) {
      return new Promise(resolve => setTimeout(resolve, milliseconds));
    },
    isClientError(error) {
      // Check various ways the error might contain status information
      if (error.status && error.status >= 400 && error.status < 500) {
        return true;
      }
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        return true;
      }
      
      // Check for common validation error patterns in message
      const validationPatterns = [
        'Name is required',
        'Valid email address is required',
        'email address is required',
        'Invalid frequency',
        'frequency format',
        'Bad Request',
        'validation',
        'invalid',
        'duplicate',
        'already exists',
        'already an approver using that name and email',
        'cannot use this email address'
      ];
      
      const errorMessage = error.message || '';
      return validationPatterns.some(pattern => 
        errorMessage.toLowerCase().includes(pattern.toLowerCase())
      );
    },
    getDetailedErrorMessage(error, row) {
      // Try to extract the most useful error message
      let message = 'Unknown error';
      
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          message = error.response.data.message;
        } else if (error.response.data.title) {
          message = error.response.data.title;
        } else if (typeof error.response.data === 'string') {
          message = error.response.data;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      // Clean up the error message - extract just the business logic error
      message = this.cleanErrorMessage(message);
      
      // Add context about the data that failed
      const name = this.getFieldValue(row, 'name');
      const email = this.getFieldValue(row, 'email');
      
      if (name || email) {
        message += ` (Name: "${name || 'N/A'}", Email: "${email || 'N/A'}")`;
      }
      
      return message;
    },
    cleanErrorMessage(message) {
      // Extract just the meaningful error message from API responses
      if (message.includes('AshoreException:')) {
        // Extract the message after "AshoreException:" and before the stack trace
        const match = message.match(/AshoreException:\s*([^\\n]+)/);
        if (match && match[1]) {
          return match[1].trim();
        }
      }
      
      // Handle duplicate contact specific errors with friendlier messages
      if (message.toLowerCase().includes('already an approver using that name and email')) {
        return 'This contact already exists with the same name and email address';
      }
      
      if (message.toLowerCase().includes('cannot use this email address')) {
        return 'Email address is already in use by another contact';
      }
      
      // For other cases, try to extract the first meaningful line
      const lines = message.split('\n');
      if (lines.length > 0) {
        return lines[0].trim();
      }
      
      return message;
    },
    isValidationError(errorMessage) {
      const validationPatterns = [
        'Validation Error',
        'Name field is empty',
        'Name is too short',
        'Name is too long', 
        'Email field is empty',
        'not a valid email',
        'Email is too long',
        'Invalid frequency',
        'frequency format',
        'Bad Request',
        'validation',
        'invalid',
        'duplicate',
        'already exists'
      ];
      
      return validationPatterns.some(pattern => 
        errorMessage.toLowerCase().includes(pattern.toLowerCase())
      );
    },
    cleanErrorMessage(errorMessage) {
      // Remove row number prefix if present
      return errorMessage.replace(/^Validation Error \(Row \d+\): /, '')
                        .replace(/^Failed after \d+ attempts \(Row \d+\): /, '');
    },
    getErrorName(error) {
      return error.data.name || this.getFieldValue(error.data, 'name') || 'N/A';
    },
    getErrorEmail(error) {
      return error.data.email || this.getFieldValue(error.data, 'email') || 'N/A';
    },
    downloadErrorReport() {
      const csvContent = this.generateErrorReportCsv();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import_errors_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    generateErrorReportCsv() {
      const headers = ['Row', 'Name', 'Email', 'Error Type', 'Error Message'];
      const rows = this.importResults.errors.map(error => [
        error.row,
        this.getErrorName(error),
        this.getErrorEmail(error),
        this.isValidationError(error.error) ? 'Validation' : 'Network/Server',
        this.cleanErrorMessage(error.error)
      ]);
      
      return [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
    },
    getFieldValue(row, fieldName) {
      // Find the header that's mapped to this field
      const headerName = Object.keys(this.columnMapping).find(header => 
        this.columnMapping[header] === fieldName
      );
      return headerName ? row[headerName] : '';
    },
    getUserDefault(fieldName) {
      // Return the user's default setting for this field
      switch (fieldName) {
        case 'frequency':
          return this.userDefaultFrequency;
        case 'timeOfDayToSend':
          return this.userDefaultTimeOfDay;
        case 'timeZone':
          return this.userDefaultTimeZone;
        default:
          return null;
      }
    },
    downloadTemplate() {
      const headers = ['name', 'email', 'frequency', 'timeOfDayToSend', 'timeZone'];
      const sampleData = [
        ['John Doe', 'john@example.com', this.userDefaultFrequency, this.userDefaultTimeOfDay, this.userDefaultTimeZone],
        ['Jane Smith', 'jane@example.com', 'Mon, Tue, Wed, Thu, Fri', '10:00', 'EST']
      ];
      
      const csvContent = [headers, ...sampleData]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    },
          close() {
        this.$bvModal.hide(this.id);
      },
      startOver() {
        this.resetModal();
      },
      goNext() {
        if (this.canGoNext) {
          this.currentStep++;
        }
      },
      goBack() {
        if (this.canGoBack) {
          this.currentStep--;
        }
      },
    isMapped(header) {
      return this.columnMapping[header] !== undefined && this.columnMapping[header] !== null;
    },
    getMappedProperty(header) {
      return this.columnMapping[header] || null;
    },
    updateMapping(header, property) {
      if (property === null || property === '') {
        // Remove mapping if "Don't import" is selected
        this.$set(this.columnMapping, header, null);
      } else {
        // Clear any existing mapping for this property to avoid duplicates
        Object.keys(this.columnMapping).forEach(key => {
          if (this.columnMapping[key] === property && key !== header) {
            this.$set(this.columnMapping, key, null);
          }
        });
        // Set new mapping
        this.$set(this.columnMapping, header, property);
      }
    },
    getColumnPreview(header) {
      const columnIndex = this.csvHeaders.indexOf(header);
      if (columnIndex === -1) return [];

      const previewData = this.csvData.map(row => {
        const value = row[header];
        return value.length > 20 ? `${value.substring(0, 20)}...` : value;
      });
      return previewData.slice(0, 3); // Show first 3 samples
    },
      handleDragEnter(event) {
        event.preventDefault();
        this.isDragOver = true;
      },
      handleDragLeave(event) {
        event.preventDefault();
        // Only set isDragOver to false if we're leaving the file input area
        if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
          this.isDragOver = false;
        }
      },
      handleDragOver(event) {
        event.preventDefault();
      },
      handleDrop(event) {
        event.preventDefault();
        this.isDragOver = false;
        // The b-form-file component will handle the actual file processing
      }
  }
};
</script>

<template>
  <b-modal
    :id="id"
    :title="modalTitle"
    size="lg"
    hide-footer
    @hidden="resetModal"
  >
    <!-- Progress Steps -->
    <div v-if="currentStep < 4" class="progress-steps mb-4">
      <div class="d-flex justify-content-between">
        <div class="step-item" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
          <div class="step-circle">
            <b-icon-check v-if="currentStep > 1" class="text-white"></b-icon-check>
            <span v-else>1</span>
          </div>
          <span class="step-label">Upload File</span>
        </div>
        
        <div class="step-connector" :class="{ active: currentStep > 1 }"></div>
        
        <div class="step-item" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
          <div class="step-circle">
            <b-icon-check v-if="currentStep > 2" class="text-white"></b-icon-check>
            <span v-else>2</span>
          </div>
          <span class="step-label">Map Columns</span>
        </div>
        
        <div class="step-connector" :class="{ active: currentStep > 2 }"></div>
        
        <div class="step-item" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
          <div class="step-circle">
            <b-icon-check v-if="currentStep > 3" class="text-white"></b-icon-check>
            <span v-else>3</span>
          </div>
          <span class="step-label">Preview & Import</span>
        </div>
      </div>
    </div>

    <div v-if="currentStep < 4">
      <!-- Step 1: File Upload -->
      <div v-if="currentStep === 1" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>Import Contacts</h5>
          <b-button variant="outline-secondary" size="sm" @click="downloadTemplate">
            Download Template
          </b-button>
        </div>
        
        <!-- Import Mode Selection -->
        <div class="mb-4">
          <h6 class="mb-3">Choose Import Type</h6>
          <div class="import-mode-selection">
            <b-form-radio-group 
              v-model="importMode" 
              :options="[
                { text: 'Create new contacts', value: 'create' },
                { text: 'Update existing contacts', value: 'update' }
              ]"
              name="import-mode"
              stacked
              @change="onImportModeChange"
            ></b-form-radio-group>
          </div>
          
          <div v-if="importMode === 'update'" class="mt-3">
            <div class="alert alert-info d-flex align-items-center">
              <b-icon-info-circle class="mr-2"></b-icon-info-circle>
              <small>
                <strong>Update Mode:</strong> Contacts will be matched by email address. 
                If a contact email doesn't exist, that row will be skipped.
              </small>
            </div>
            
            <div v-if="isLoadingContacts" class="text-center">
              <b-spinner small class="mr-2"></b-spinner>
              Loading existing contacts...
            </div>
            
            <div v-else-if="existingContacts.length > 0" class="text-success">
              <b-icon-check-circle class="mr-1"></b-icon-check-circle>
              Loaded {{ existingContacts.length }} existing contacts for matching
            </div>
          </div>
        </div>
        
        <div 
          class="file-drop-zone"
          :class="{ 'drag-over': isDragOver }"
          @dragenter="handleDragEnter"
          @dragleave="handleDragLeave"
          @dragover="handleDragOver"
          @drop="handleDrop"
        >
          <b-form-file
            ref="fileInput"
            v-model="csvFile"
            accept=".csv"
            placeholder="Choose a CSV file..."
            drop-placeholder="Drop CSV file here..."
            @change="handleFileUpload"
            :disabled="isLoading || isImporting"
          ></b-form-file>
        </div>
        
        <small class="text-muted">
          CSV file should contain at least Name and Email columns. 
          <a href="#" @click.prevent="downloadTemplate">Download a template</a> to get started.
        </small>
      </div>

      <!-- File Selected Header - Show on steps 2 and 3 -->
      <div v-if="(currentStep === 2 || currentStep === 3) && hasValidFile && !isLoading" class="mb-4">
        <div class="file-selected-header">
          <div class="d-flex align-items-center">
            <b-icon-file-earmark-check class="text-success mr-2" font-scale="1.5"></b-icon-file-earmark-check>
            <div>
              <h5 class="mb-0">{{ csvFile.name }}</h5>
              <small class="text-muted">{{ csvData.length }} records found</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2">Reading CSV file...</p>
      </div>

      <!-- Step 2: Column Mapping Section -->
      <div v-if="currentStep === 2 && hasValidFile && !isLoading" class="mb-4">
        <div class="mb-3">
          <h5>Map your CSV columns to contact properties</h5>
          <p class="text-muted mb-0">Select which contact property each CSV column should map to.</p>
        </div>
        
        <div class="mapping-container">
          <!-- Mapping Header -->
          <div class="mapping-header row">
            <div class="col-3">
              <small class="text-uppercase text-muted font-weight-bold">Column Header from File</small>
            </div>
            <div class="col-3">
              <small class="text-uppercase text-muted font-weight-bold">Preview Information</small>
            </div>
            <div class="col-2 text-center">
              <small class="text-uppercase text-muted font-weight-bold">Mapped</small>
            </div>
            <div class="col-4">
              <small class="text-uppercase text-muted font-weight-bold">Contact Property</small>
            </div>
          </div>

          <!-- Mapping Rows -->
          <div v-for="header in csvHeaders" :key="header" class="mapping-row row align-items-center">
            <!-- Column Header -->
            <div class="col-3">
              <div class="column-header">{{ header }}</div>
            </div>

            <!-- Preview Data -->
            <div class="col-3">
              <div class="preview-data">
                <div v-for="(sample, index) in getColumnPreview(header)" :key="index" class="preview-item">
                  {{ sample || '--' }}
                </div>
              </div>
            </div>

            <!-- Mapped Status -->
            <div class="col-2 text-center">
              <div class="mapped-status">
                <b-icon-check-circle-fill 
                  v-if="isMapped(header)" 
                  class="text-success mapped-icon"
                ></b-icon-check-circle-fill>
                <div v-else class="unmapped-circle"></div>
              </div>
            </div>

            <!-- Property Mapping -->
            <div class="col-4">
              <div class="property-mapping">
                <b-form-select
                  :value="getMappedProperty(header)"
                  @change="updateMapping(header, $event)"
                  class="property-select"
                  :class="{ 'is-mapped': isMapped(header) }"
                >
                  <option :value="null">Don't import</option>
                  <optgroup label="Contact Properties">
                    <option 
                      v-for="(field, fieldName) in requiredFields" 
                      :key="fieldName"
                      :value="fieldName"
                    >
                      {{ field.label }}{{ field.required ? ' *' : '' }}
                    </option>
                  </optgroup>
                </b-form-select>
              </div>
            </div>
          </div>
        </div>

        <!-- Unmapped Columns Notice -->
        <div v-if="unmappedRequiredFields.length > 0" class="alert alert-warning mt-3">
          <b-icon-exclamation-triangle class="mr-2"></b-icon-exclamation-triangle>
          <strong>Required fields not mapped:</strong> 
          {{ unmappedRequiredFields.map(f => requiredFields[f].label).join(', ') }}
        </div>

        <div v-if="unmappedColumnsCount > 0" class="unmapped-notice mt-3">
          <small class="text-muted">
            <b-icon-info-circle class="mr-1"></b-icon-info-circle>
            You have {{ unmappedColumnsCount }} unmapped column{{ unmappedColumnsCount > 1 ? 's' : '' }}. 
            Unmapped columns will be ignored during import.
          </small>
        </div>
      </div>

      <!-- Step 3: Preview Section -->
      <div v-if="currentStep === 3 && hasValidFile && !isLoading" class="mb-4">
        <div class="mb-3">
          <h5>Preview {{ importMode === 'create' ? 'Create' : 'Update' }} Data</h5>
          <p class="text-muted mb-0">Review how your data will be {{ importMode === 'create' ? 'created' : 'updated' }} (showing first 5 rows).</p>
        </div>
        
        <div class="preview-container">
          <div class="table-responsive">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th v-for="(field, fieldName) in requiredFields" :key="fieldName">
                    {{ field.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in previewData" :key="index">
                  <td v-for="(field, fieldName) in requiredFields" :key="fieldName">
                    <span v-if="getFieldValue(row, fieldName)" class="text-dark">
                      {{ getFieldValue(row, fieldName) }}
                    </span>
                    <span v-else-if="getUserDefault(fieldName)" class="text-muted">
                      {{ getUserDefault(fieldName) }} <small>(user default)</small>
                    </span>
                    <span v-else class="text-muted">--</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div class="import-summary mt-3">
          <div class="d-flex align-items-center text-success">
            <b-icon-check-circle class="mr-2"></b-icon-check-circle>
            <strong>Ready to {{ importMode === 'create' ? 'create' : 'update' }} {{ csvData.length }} contact{{ csvData.length !== 1 ? 's' : '' }}</strong>
          </div>
          
        </div>
      </div>

      <!-- Import Progress -->
      <div v-if="isImporting" class="mb-4">
        <h5>Importing Contacts...</h5>
        <b-progress :value="importProgress" :max="100" class="mb-3" variant="success" striped animated></b-progress>
        
        <div class="import-stats">
          <div class="row text-center">
            <div class="col-md-3">
              <div class="stat-card">
                <div class="stat-number">{{ importResults.successful + importResults.failed }}</div>
                <div class="stat-label">Processed</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="stat-card">
                <div class="stat-number">{{ importResults.total - (importResults.successful + importResults.failed) }}</div>
                <div class="stat-label">Remaining</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="stat-card text-success">
                <div class="stat-number">{{ importResults.successful }}</div>
                <div class="stat-label">Successful</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="stat-card text-danger">
                <div class="stat-number">{{ importResults.failed }}</div>
                <div class="stat-label">Failed</div>
              </div>
            </div>
          </div>
        </div>
        

      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-between">
        <div>
          <!-- Back/Cancel Button -->
          <b-button 
            v-if="canGoBack" 
            variant="outline-secondary" 
            @click="goBack" 
            :disabled="isImporting"
          >
            <b-icon-arrow-left class="mr-1"></b-icon-arrow-left>
            Back
          </b-button>
          <b-button 
            v-else
            variant="secondary" 
            @click="close" 
            :disabled="isImporting"
          >
            Cancel
          </b-button>
        </div>
        
        <div>
          <!-- Next Button (Steps 1-2) -->
          <b-button 
            v-if="currentStep < 3 && canGoNext"
            variant="primary" 
            @click="goNext"
            :disabled="!canGoNext"
          >
            Next
            <b-icon-arrow-right class="ml-1"></b-icon-arrow-right>
          </b-button>
          
          <!-- Import Button (Step 3) -->
          <b-button 
            v-if="currentStep === 3"
            variant="success" 
            @click="startImport" 
            :disabled="!hasValidMapping || isImporting"
          >
            <b-spinner v-if="isImporting" small class="mr-2"></b-spinner>
            {{ isImporting ? (importMode === 'create' ? 'Creating...' : 'Updating...') : (importMode === 'create' ? 'Create Contacts' : 'Update Contacts') }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Step 4: Results Section -->
    <div v-if="currentStep === 4">
      <h5>Import Results</h5>
      
      <div class="alert alert-success" v-if="importResults.successful > 0">
        <strong>Successfully imported {{ importResults.successful }} contacts!</strong>
      </div>
      
      <div class="alert alert-warning" v-if="importResults.failed > 0">
        <strong>{{ importResults.failed }} contacts failed to import.</strong>
        
        <div v-if="importResults.errors.length > 0" class="mt-3">
          <h6>
            <b-icon-exclamation-triangle class="text-warning mr-2"></b-icon-exclamation-triangle>
            Import Errors ({{ importResults.errors.length }})
          </h6>
          
          <!-- Error Categories -->
          <div class="error-summary mb-3">
            <div class="row">
              <div class="col-md-6">
                <small class="text-muted">
                  <strong>Validation Errors:</strong> {{ validationErrors.length }}<br>
                  <strong>Network/Server Errors:</strong> {{ networkErrors.length }}
                </small>
              </div>
              <div class="col-md-6">
                <small class="text-muted">
                  <em>Validation errors require data fixes. Network errors may succeed if retried.</em>
                </small>
              </div>
            </div>
          </div>
          
          <div class="table-responsive">
            <table class="table table-sm table-bordered">
              <thead class="table-light">
                <tr>
                  <th width="60px">Row</th>
                  <th width="120px">Name</th>
                  <th width="120px">Email</th>
                  <th width="80px">Type</th>
                  <th>Error Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="error in importResults.errors.slice(0, 10)" :key="error.row">
                  <td class="text-center">
                    <span class="badge badge-secondary">{{ error.row }}</span>
                  </td>
                  <td class="small">{{ getErrorName(error) }}</td>
                  <td class="small">{{ getErrorEmail(error) }}</td>
                  <td>
                    <span 
                      :class="isValidationError(error.error) ? 'badge badge-warning' : 'badge badge-danger'"
                    >
                      {{ isValidationError(error.error) ? 'Data' : 'Server' }}
                    </span>
                  </td>
                  <td class="small">
                    <span :class="isValidationError(error.error) ? 'text-warning' : 'text-danger'">
                      {{ cleanErrorMessage(error.error) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="d-flex justify-content-between align-items-center">
            <small v-if="importResults.errors.length > 10" class="text-muted">
              Showing first 10 errors out of {{ importResults.errors.length }}
            </small>
            <b-button 
              variant="outline-primary" 
              size="sm" 
              @click="downloadErrorReport"
            >
              <b-icon-download class="mr-1"></b-icon-download>
              Download Error Report
            </b-button>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between">
        <div></div>
        <b-button variant="primary" @click="close">
          Close
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}

.text-danger {
  color: #dc3545 !important;
}

.alert {
  border-radius: 6px;
}

.import-mode-selection {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  background-color: #fafafa;
}

.import-mode-selection .custom-control {
  margin-bottom: 8px;
}

.import-mode-selection .custom-control:last-child {
  margin-bottom: 0;
}

 .table th {
   background-color: #f8f9fa;
   font-weight: 600;
 }

 .file-drop-zone {
   transition: all 0.2s ease;
   border-radius: 6px;
 }

 .file-drop-zone.drag-over {
   background-color: #e3f2fd;
   border: 2px dashed #2196f3;
   padding: 4px;
 }

 .file-drop-zone.drag-over .custom-file-label {
   background-color: #ffffff;
   border-color: #2196f3;
   color: #2196f3;
 }

 .file-selected-header {
   padding: 15px 20px;
   background-color: #f8f9fa;
   border: 1px solid #e9ecef;
   border-radius: 8px;
   margin-bottom: 0;
 }

 .file-selected-header h5 {
   color: #343a40;
   font-weight: 600;
 }

 .preview-container {
   border: 1px solid #e9ecef;
   border-radius: 8px;
   padding: 15px;
   background-color: #fafafa;
 }

 .import-summary {
   padding: 12px 16px;
   background-color: #d4edda;
   border: 1px solid #c3e6cb;
   border-radius: 6px;
 }

 .progress-steps {
   padding: 20px 0;
   border-bottom: 1px solid #e9ecef;
 }

 .step-item {
   display: flex;
   flex-direction: column;
   align-items: center;
   position: relative;
   color: #6c757d;
   transition: color 0.3s ease;
 }

 .step-item.active {
   color: #007bff;
 }

 .step-item.completed {
   color: #28a745;
 }

 .step-circle {
   width: 32px;
   height: 32px;
   border-radius: 50%;
   background-color: #e9ecef;
   border: 2px solid #e9ecef;
   display: flex;
   align-items: center;
   justify-content: center;
   font-weight: 600;
   font-size: 14px;
   color: #6c757d;
   transition: all 0.3s ease;
   margin-bottom: 8px;
 }

 .step-item.active .step-circle {
   background-color: #007bff;
   border-color: #007bff;
   color: white;
 }

 .step-item.completed .step-circle {
   background-color: #28a745;
   border-color: #28a745;
   color: white;
 }

 .step-label {
   font-size: 12px;
   font-weight: 500;
   text-align: center;
   white-space: nowrap;
 }

 .step-connector {
   flex: 1;
   height: 2px;
   background-color: #e9ecef;
   margin: 0 20px;
   margin-top: 16px;
   transition: background-color 0.3s ease;
 }

 .step-connector.active {
   background-color: #28a745;
 }

 .import-stats {
   padding: 15px;
   background-color: #f8f9fa;
   border-radius: 8px;
   border: 1px solid #e9ecef;
 }

 .stat-card {
   padding: 8px;
 }

 .stat-number {
   font-size: 1.5rem;
   font-weight: 600;
   line-height: 1.2;
 }

 .stat-label {
   font-size: 0.875rem;
   color: #6c757d;
   font-weight: 500;
 }

 .error-summary {
   padding: 12px 16px;
   background-color: #fff8e1;
   border: 1px solid #ffecb3;
   border-radius: 6px;
   margin-bottom: 16px;
 }

 .table-light th {
   background-color: #f8f9fa;
   border-bottom: 2px solid #dee2e6;
   font-weight: 600;
   font-size: 0.875rem;
 }

 .badge {
   font-size: 0.75rem;
 }
 
 .mapping-container {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  max-height: 400px;
  overflow-y: auto;
}

.mapping-header {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 10px;
}

.mapping-row {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.mapping-row:last-child {
  border-bottom: none;
}

.column-header {
  font-weight: 600;
  color: #343a40;
  font-size: 0.9rem;
}

.preview-data {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 5px;
}

.preview-item {
  margin-bottom: 3px;
}

 .mapped-status {
   display: flex;
   align-items: center;
   justify-content: center;
   width: 24px;
   height: 24px;
   margin: 0 auto;
 }

.mapped-icon {
  font-size: 1.2rem;
}

 .unmapped-circle {
   width: 16px;
   height: 16px;
   border-radius: 50%;
   background-color: #e9ecef;
   border: 2px solid #ced4da;
 }

.property-mapping .form-control {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.property-select.is-mapped {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

 .unmapped-notice {
   margin-top: 15px;
   padding: 12px 16px;
   background-color: #f8f9fa;
   border: 1px solid #e9ecef;
   border-radius: 6px;
   color: #6c757d;
   font-size: 0.875rem;
 }
</style> 